import * as Sentry from "@sentry/react";
import { StrictMode, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { Button } from "./components/ui/button.tsx";
import { Toaster } from "./components/ui/feedback/sonner.tsx";
import { isLocal, isProduction } from "./config.ts";
import { bootstrapGoogleTag } from "./lib/analytics/googleTag.ts";
import { gbInitPromise } from "./lib/feature-management/index.ts";
import "./index.css";
import { applyEnvironmentFavicon } from "./lib/utils/applyEnvironmentFavicon.ts";
import { copyTextToClipboard } from "./lib/utils/clipboard.ts";
import { cn } from "./lib/utils/cn.ts";
import {
  reloadPageForStaleDynamicImportError,
  setupVitePreloadErrorHandler,
} from "./lib/utils/vitePreloadError.ts";
import Providers from "./providers.tsx";
import { CustomLoader } from "./shared/CustomLoader.tsx";
import TanStackRouter from "./tanstack-router.tsx";

applyEnvironmentFavicon();

if (!isLocal) {
  bootstrapGoogleTag();
}

/**
 * Waits for GrowthBook to finish loading features before rendering the router.
 * Avoids a flash where the app reads feature flags before GrowthBook is ready.
 */
const AppRouter = () => {
  const [gbReady, setGbReady] = useState(false);

  useEffect(() => {
    gbInitPromise.finally(() => setGbReady(true));
  }, []);

  if (!gbReady) return <CustomLoader />;

  return <TanStackRouter />;
};

interface ErrorDetailsProps {
  error: Error | unknown;
  componentStack: string;
}

const ErrorDetails: React.FC<ErrorDetailsProps> = ({
  error,
  componentStack,
}) => {
  const errorString =
    error instanceof Error ? error.toString() : JSON.stringify(error);

  return (
    <details
      className={cn(
        "bg-rose-50",
        "p-4",
        "rounded-sm",
        "border",
        "border-rose-200",
        "shadow-xs",
        "mb-8",
        "text-rose-900",
      )}
    >
      <summary
        className={cn("cursor-pointer", "text-lg", "font-bold", "text-red-600")}
      >
        Error details (Non Production Only)
      </summary>
      <pre className={cn("overflow-x-auto", "p-2", "bg-red-50", "rounded-sm")}>
        <code
          className={cn(
            "whitespace-pre",
            "break-all",
            "text-xs",
            "text-red-600",
          )}
        >
          {errorString}
          <br />
          {componentStack}
        </code>
      </pre>
    </details>
  );
};

interface ErrorHandlerProps {
  error: Error | unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

const ErrorHandler: React.FC<ErrorHandlerProps> = ({
  error,
  componentStack,
  eventId,
  resetError,
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isLocal) {
      reloadPageForStaleDynamicImportError(error);
    }
  }, [error]);

  const handleCopy = async () => {
    const didCopy = await copyTextToClipboard(eventId);
    if (didCopy) {
      setCopied(true);
    }
  };

  return (
    <div>
      <div
        className={cn(
          "fixed",
          "top-1/2",
          "left-1/2",
          "transform",
          "-translate-x-1/2",
          "-translate-y-1/2",
          "z-50",
          "bg-white",
          "rounded-sm",
          "shadow-md",
          "py-8",
          "px-10",
          "border",
          "border-slate-200",
          "max-w-4xl",
          "w-4/5",
          "mx-4",
        )}
      >
        <h1
          className={cn(
            "text-3xl",
            "font-serif",
            "text-gray-800",
            "font-bold",
            "mb-4",
          )}
        >
          Something Went Wrong!
        </h1>
        <p className={cn("text", "text-slate-600", "mb-8")}>
          Don't worry, we've been notified and our team is already working
          diligently to resolve the issue. We appreciate your patience and will
          get back to you soon.
        </p>
        {!isProduction && (
          <ErrorDetails error={error} componentStack={componentStack} />
        )}
        <Button onPress={resetError}>Refresh Page</Button>
        <Button
          className="ml-2"
          variant="secondary"
          onPress={() => window.location.replace("/")}
        >
          Go Home
        </Button>
      </div>
      <p
        className={cn(
          "text-xs",
          "text-slate-400",
          "mt-4 absolute",
          "bottom-0 left-2",
        )}
      >
        If you'd like to report this issue, please copy and share the following
        event ID with our support team:
        <br />
        <code className={cn("text-xs")}>
          {eventId}
          <Button
            className={cn("text-xs", "ml-2", "cursor-pointer")}
            onPress={handleCopy}
            variant={"link"}
          >
            {copied ? "Copied!" : "Copy"}
          </Button>
        </code>
      </p>
    </div>
  );
};

const App = () => {
  useEffect(() => setupVitePreloadErrorHandler(), []);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, componentStack, eventId }) => (
        <ErrorHandler
          error={error}
          resetError={resetError}
          componentStack={componentStack}
          eventId={eventId}
        />
      )}
    >
      <Providers>
        <AppRouter />
        <Toaster />
      </Providers>
    </Sentry.ErrorBoundary>
  );
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
